import { useEffect } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import ServerSideGlobalFilter from "../../../layouts/DataTables/ServerSideDataTable/ServerSideGlobalFilter";

const getStableRowKey = (row) =>
  row.original?.transactionId || row.original?.id || row.id;

const SCRV2TransactionReversalDataTable = ({
  searchChanged,
  pageNo,
  pageSizeChange,
  myPageSize,
  totalRecords,
  goToPreviousPage,
  goToNextPage,
  columns,
  data,
}) => {
  const resolvedPageSize = Number(myPageSize) > 0 ? Number(myPageSize) : 20;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    setPageSize,
    state: { pageIndex, pageSize },
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      searchChanged,
      pageNo,
      pageSizeChange,
      myPageSize,
      totalRecords,
      goToPreviousPage,
      goToNextPage,
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: resolvedPageSize },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    setPageSize(resolvedPageSize);
  }, [resolvedPageSize, setPageSize]);

  const checkLastPage = () => {
    if (Math.floor(totalRecords % myPageSize) === 0) {
      if (pageNo + 1 < Math.floor(totalRecords / myPageSize)) {
        goToNextPage(pageNo + 1, myPageSize);
      }
    } else if (pageNo < Math.floor(totalRecords / myPageSize)) {
      goToNextPage(pageNo + 1, myPageSize);
    }
  };

  return (
    <div style={{ overflow: "hidden", overflowX: "scroll", minHeight: "350px" }}>
      <ServerSideGlobalFilter
        totalRecords={totalRecords}
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
        searchChanged={searchChanged}
      />
      <table
        className="table striped bordered hover scr-v2-reversal-table"
        {...getTableProps()}
        style={{ minHeight: "330pxs" }}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={column.id}
                  style={{ verticalAlign: "top", whiteSpace: "nowrap" }}
                >
                  {column.render("Header")}
                  <span>{column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} style={{ Height: "auto" }}>
          {page && page.length > 0 ? (
            page.map((row) => {
              prepareRow(row);
              const rowKey = getStableRowKey(row);

              return (
                <tr
                  {...row.getRowProps()}
                  className="table-hover-class"
                  key={rowKey}
                  style={{ cursor: "pointer" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={`${rowKey}-${cell.column.id}`}
                      style={{ verticalAlign: "top" }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={headerGroups[0].headers.length} style={{ textAlign: "center" }}>
                No record found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <ul className="pagination float-right pagination-scroll">
        <select
          className="form-control"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            pageSizeChange(Number(e.target.value));
          }}
          style={{ width: "120px", height: "38px" }}
        >
          {[5, 10, 20, 30, 40, 50, 100].map((optionSize) => (
            <option key={optionSize} value={optionSize}>
              Show {optionSize}
            </option>
          ))}
        </select>
        <li
          className="page-item pointer"
          onClick={pageNo === 0 ? null : () => goToPreviousPage(0, pageSize)}
          disabled={!canPreviousPage}
        >
          <a className="page-link">&laquo;</a>
        </li>
        <li
          className="page-item pointer"
          onClick={pageNo === 0 ? null : () => goToPreviousPage(pageNo - 1, myPageSize)}
          disabled={!canPreviousPage}
        >
          <a className="page-link">&lt;</a>
        </li>
        <li className="page-item pointer" onClick={checkLastPage} disabled={!canNextPage}>
          <a className="page-link">&gt;</a>
        </li>
        <li
          className="page-item pointer"
          onClick={
            pageNo > Math.floor(totalRecords / myPageSize) - 1
              ? null
              : () => {
                  goToNextPage(
                    Math.floor(totalRecords % myPageSize) === 0
                      ? Math.floor(totalRecords / myPageSize) - 1
                      : Math.floor(totalRecords / myPageSize),
                    myPageSize,
                  );
                }
          }
          disabled={!canNextPage}
        >
          <a className="page-link">&raquo;</a>
        </li>
        <li>
          <a className="page-link" style={{ width: "auto" }}>
            Page
            <strong>
              {" "}
              {pageNo + 1} of{" "}
              {totalRecords == null || totalRecords === 0
                ? 1
                : Math.ceil(totalRecords / myPageSize)}
            </strong>
          </a>
        </li>
        <li>
          <a className="page-link">
            <input
              className="form-control"
              type="number"
              min={1}
              max={Math.ceil(totalRecords / myPageSize)}
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const nextPage = e.target.value ? Number(e.target.value) - 1 : 0;
                if (nextPage < 0) {
                  return;
                }
                goToNextPage(nextPage, myPageSize);
              }}
              onInput={(e) => {
                const val = parseInt(e.target.value, 10);

                if (val < 1) {
                  e.target.value = 1;
                }

                if (val > Math.ceil(totalRecords / myPageSize)) {
                  e.target.value = Math.ceil(totalRecords / myPageSize);
                }
              }}
              style={{ width: "100px", height: "20px" }}
            />
          </a>
        </li>
      </ul>
    </div>
  );
};

export default SCRV2TransactionReversalDataTable;
